/**
 * Onyx character emotion system — visual states separate from chat logic.
 * Map: emotion → image asset under /mascots/onyx/
 */

import {
  detectHostilityTier,
  detectOnyxChallenge,
  detectOnyxSafetyTopic,
} from './onyxHostilityLexicon';

export const ONYX_EMOTIONS = [
  'SLEEPING',
  'NEUTRAL',
  'HAPPY',
  'LAUGHING',
  'ANGRY',
  'ANNOYED',
  'SAD',
  'DISAPPOINTED',
  'SURPRISED',
  'THINKING',
  'PLAYFUL',
  'CONFIDENT',
] as const;

export type OnyxEmotion = (typeof ONYX_EMOTIONS)[number];

/** Legacy mood aliases used by older widget code. */
export type OnyxMood =
  | 'sleep'
  | 'waking'
  | 'awake'
  | 'thinking'
  | Lowercase<OnyxEmotion>
  | OnyxEmotion;

export type OnyxAnimation =
  | 'idle-sleep'
  | 'wake'
  | 'bounce'
  | 'shake'
  | 'pop'
  | 'fade-slow'
  | 'sway'
  | 'nod'
  | 'talk'
  | 'none';

/** Single config area: emotion → PNG filename (in /mascots/onyx/). */
export const ONYX_EMOTION_IMAGES: Record<OnyxEmotion, string> = {
  /** Idle sleep — FAB WebP léger (évite PNG 1.4 Mo sur mobile). */
  SLEEPING: 'fab/onyx_fab_sleep.webp',
  NEUTRAL: 'neutral.png',
  HAPPY: 'happy.png',
  LAUGHING: 'laughing.png',
  ANGRY: 'angry.png',
  ANNOYED: 'annoyed.png',
  SAD: 'sad.png',
  DISAPPOINTED: 'disappointed.png',
  SURPRISED: 'surprised.png',
  THINKING: 'thinking.png',
  PLAYFUL: 'playful.png',
  CONFIDENT: 'confident.png',
};

export const ONYX_EMOTION_ANIMATION: Record<OnyxEmotion, OnyxAnimation> = {
  SLEEPING: 'idle-sleep',
  NEUTRAL: 'none',
  HAPPY: 'bounce',
  LAUGHING: 'bounce',
  ANGRY: 'shake',
  ANNOYED: 'shake',
  SAD: 'fade-slow',
  DISAPPOINTED: 'fade-slow',
  SURPRISED: 'pop',
  THINKING: 'nod',
  PLAYFUL: 'sway',
  CONFIDENT: 'bounce',
};

export const ONYX_STATUS_LABEL: Record<OnyxEmotion, string> = {
  SLEEPING: 'Sleeping',
  NEUTRAL: 'Awake',
  HAPPY: 'Happy',
  LAUGHING: 'Laughing',
  ANGRY: 'Angry',
  ANNOYED: 'Annoyed',
  SAD: 'Sad',
  DISAPPOINTED: 'Disappointed',
  SURPRISED: 'Surprised',
  THINKING: 'Thinking',
  PLAYFUL: 'Playful',
  CONFIDENT: 'Confident',
};

const ONYX_ASSET_BASE = '/mascots/onyx';

export function onyxImageUrl(emotion: OnyxEmotion): string {
  return `${ONYX_ASSET_BASE}/${ONYX_EMOTION_IMAGES[emotion]}`;
}

export function isOnyxEmotion(value: unknown): value is OnyxEmotion {
  return (
    typeof value === 'string' &&
    (ONYX_EMOTIONS as readonly string[]).includes(value)
  );
}

/** Normalize legacy moods + lowercase aliases → OnyxEmotion. */
export function resolveOnyxEmotion(mood?: OnyxMood | string | null): OnyxEmotion {
  if (!mood) return 'NEUTRAL';
  const raw = String(mood).trim();
  const upper = raw.toUpperCase();
  if (isOnyxEmotion(upper)) return upper;

  switch (raw.toLowerCase()) {
    case 'sleep':
    case 'sleeping':
      return 'SLEEPING';
    case 'waking':
    case 'awake':
    case 'normal':
    case 'neutral':
      return 'NEUTRAL';
    case 'thinking':
      return 'THINKING';
    case 'happy':
      return 'HAPPY';
    case 'laughing':
    case 'laugh':
      return 'LAUGHING';
    case 'angry':
      return 'ANGRY';
    case 'annoyed':
      return 'ANNOYED';
    case 'sad':
      return 'SAD';
    case 'disappointed':
      return 'DISAPPOINTED';
    case 'surprised':
    case 'surprise':
      return 'SURPRISED';
    case 'playful':
    case 'mischievous':
      return 'PLAYFUL';
    case 'confident':
      return 'CONFIDENT';
    default:
      return 'NEUTRAL';
  }
}

export type OnyxUserIntent =
  | 'joke'
  | 'insult'
  | 'abuse'
  | 'challenge'
  | 'safety'
  | 'compliment'
  | 'sad'
  | 'dissatisfied'
  | 'exciting'
  | 'confusing'
  | 'annoying'
  | 'question'
  | 'help'
  | 'greeting'
  | 'casual';

/** User messages with clear tone — should override generic AI emotion (e.g. PLAYFUL on insult). */
const STRONG_USER_INTENTS = new Set<OnyxUserIntent>([
  'insult',
  'abuse',
  'challenge',
  'safety',
  'compliment',
  'sad',
  'dissatisfied',
  'joke',
  'annoying',
  'exciting',
  'greeting',
]);

interface IntentRule {
  intent: OnyxUserIntent;
  emotion: OnyxEmotion;
  patterns: RegExp[];
}

const INTENT_RULES: IntentRule[] = [
  {
    intent: 'joke',
    emotion: 'LAUGHING',
    patterns: [
      /\b(lol|lmao|haha|hahaha|😂|🤣|💀|joke|funny|hilarious|burned water|stupid me)\b/i,
      /\b(mdr|ptdr|lol|rigole|blague|mort de rire)\b/i,
    ],
  },
  {
    intent: 'annoying',
    emotion: 'ANNOYED',
    patterns: [
      /\b(annoying|annoy|boring|whatever|meh|blah blah|test(ing)? (my )?patience|ugh|argh)\b/i,
      /\b(énervant|relou|bof|peu importe|teste ma patience)\b/i,
    ],
  },
  {
    intent: 'dissatisfied',
    emotion: 'DISAPPOINTED',
    patterns: [
      /\b(not satisfied|not happy with|disappointed|unsatisfied|not helpful|didn't help)\b/i,
      /\b(waste of time|doesn't work|does not work|not what i wanted|you failed|wrong answer)\b/i,
      /\b(déçu|pas content|pas satisfait|ça ne marche pas|inutile)\b/i,
    ],
  },
  {
    intent: 'sad',
    emotion: 'SAD',
    patterns: [
      /\b(sad|cry|crying|depressed|lonely|miss|failed|unfortunate|😔|😭|😢)\b/i,
      /\b(triste|pleure|raté|malheureux)\b/i,
    ],
  },
  {
    intent: 'exciting',
    emotion: 'SURPRISED',
    patterns: [
      /\b(omg|wait what|no way|incredible|amazing|wow|whoa|😲|😳|🎉|holy)\b/i,
      /\b(incroyable|waouh|sérieux|pas possible|oh mon dieu)\b/i,
    ],
  },
  {
    intent: 'compliment',
    emotion: 'HAPPY',
    patterns: [
      /\b(love you|love this|love komby|awesome|great job|thank(s| you)?|you're the best|amazing|cool|perfect|excellent)\b/i,
      /\b(i'm happy|im happy|so happy|feeling good|well done|nice one)\b/i,
      /\b(you('re| are)\s+(so\s+)?(kind|nice|sweet|great|awesome|amazing|the best|helpful|funny))\b/i,
      /\bsuch a (kind|nice|sweet|good)\b/i,
      /\b(merci|super|génial|bravo|t[' ]es le meilleur|cool|parfait|trop\s*bien)\b/i,
      /\b(t[' ]es\s+(trop\s+)?(super|gentil|gentille|cool|genial|génial|merveilleux|formidable|sympa|mignon|mignonne))\b/i,
      /\b(tu\s+es\s+(trop\s+)?(super|gentil|gentille|cool|genial|génial|sympa))\b/i,
      /\b(il\s+est\s+(trop\s+)?(super|gentil|cool)|onyx\s+est\s+(trop\s+)?(super|gentil|cool))\b/i,
      /\b(eres\s+(muy\s+)?(amable|genial|bueno|buena|el\s+mejor)|qué\s+bueno|que\s+bueno)\b/i,
      /\b(você\s+é\s+(muito\s+)?(legal|gentil|incrível)|voce\s+e\s+|obrigad[oa])\b/i,
      /\b(sei\s+(troppo\s+)?(gentile|bravo|fantastico)|grazie)\b/i,
      /\b(du\s+bist\s+(so\s+)?(nett|toll|super|freundlich)|danke)\b/i,
      /(ありがとう|優しい|すごい|真棒|谢谢|太棒了|착해|고마워|شكرا|لطيف)/i,
    ],
  },
  {
    intent: 'confusing',
    emotion: 'THINKING',
    patterns: [
      /\b(confused|confusing|don't understand|huh\??|what do you mean|\?\?\?)\b/i,
      /\b(comprends pas|confus|hein\??|c'est quoi)\b/i,
    ],
  },
  {
    intent: 'greeting',
    emotion: 'HAPPY',
    patterns: [
      /^(hi|hello|hey|yo|sup|good (morning|afternoon|evening))[!?.]*$/i,
      /^(bonjour|salut|coucou|bonsoir|hey)[!?.]*$/i,
    ],
  },
  {
    intent: 'help',
    emotion: 'CONFIDENT',
    patterns: [
      /\b(how (do|can|to)|help me|show me|guide|steps?|tutorial)\b/i,
      /\b(comment (faire|je)|aide[- ]moi|montre|étapes?|tutoriel)\b/i,
    ],
  },
  {
    intent: 'question',
    emotion: 'THINKING',
    patterns: [/\?$/, /^(what|where|when|why|who|which|is|are|can|does|do)\b/i],
  },
];

export function detectOnyxIntent(message: string): OnyxUserIntent {
  const text = message.trim();
  if (!text) return 'casual';

  if (detectOnyxSafetyTopic(text)) return 'safety';
  const hostility = detectHostilityTier(text);
  if (hostility.tier === 'B') return 'abuse';
  if (hostility.tier === 'A') return 'insult';
  if (detectOnyxChallenge(text)) return 'challenge';

  for (const rule of INTENT_RULES) {
    if (rule.patterns.some((p) => p.test(text))) return rule.intent;
  }
  return 'casual';
}

/** Infer Onyx emotion from the user message (before / while answering). */
export function detectOnyxEmotionFromUserMessage(message: string): OnyxEmotion {
  const text = message.trim();
  if (!text) return 'NEUTRAL';

  if (detectOnyxSafetyTopic(text)) return 'SAD';
  const hostility = detectHostilityTier(text);
  if (hostility.tier === 'B' || hostility.tier === 'A') return 'ANGRY';
  if (detectOnyxChallenge(text)) return 'CONFIDENT';

  for (const rule of INTENT_RULES) {
    if (rule.patterns.some((p) => p.test(text))) return rule.emotion;
  }
  return 'NEUTRAL';
}

/** Infer emotion from assistant reply tone when user message is neutral. */
function inferEmotionFromAssistantReply(text: string): OnyxEmotion | null {
  const reply = text.toLowerCase();
  if (/😂|🤣|haha|lol|hilarious|💀|funny/.test(reply)) return 'LAUGHING';
  if (/sorry|unfortunately|can't help|aw man|désolé|malheureusement/.test(reply)) {
    return 'SAD';
  }
  if (/disappoint|not ideal|wish i could|doesn't quite/.test(reply)) return 'DISAPPOINTED';
  if (/😑|patience|annoy|relou|seriously\?/.test(reply)) return 'ANNOYED';
  if (/😲|😳|wait|what\?!|waouh|no way/.test(reply)) return 'SURPRISED';
  if (/😏|heh|naughty|mischief|cheeky/.test(reply)) return 'PLAYFUL';
  if (/happy to help|glad|great question|let me show|here('s| is) how/.test(reply)) {
    return 'CONFIDENT';
  }
  if (/welcome|hello|hey there|bonjour|salut/.test(reply)) return 'HAPPY';
  return null;
}

/**
 * Pick the Onyx face for an assistant message.
 * Priority: strong user tone → AI explicit → reply tone → user weak → NEUTRAL.
 */
export function pickOnyxEmotion(options: {
  explicit?: string | null;
  userMessage?: string;
  assistantText?: string;
  busy?: boolean;
}): OnyxEmotion {
  if (options.busy) return 'THINKING';

  const userMessage = (options.userMessage || '').trim();
  const userIntent = userMessage ? detectOnyxIntent(userMessage) : 'casual';
  const fromUser = userMessage ? detectOnyxEmotionFromUserMessage(userMessage) : 'NEUTRAL';

  if (STRONG_USER_INTENTS.has(userIntent) && fromUser !== 'NEUTRAL') {
    return fromUser;
  }

  if (options.explicit && isOnyxEmotion(String(options.explicit).toUpperCase())) {
    return String(options.explicit).toUpperCase() as OnyxEmotion;
  }

  const fromReply = options.assistantText
    ? inferEmotionFromAssistantReply(options.assistantText)
    : null;
  if (fromReply) return fromReply;

  if (fromUser !== 'NEUTRAL') return fromUser;

  return 'NEUTRAL';
}

/** Emotion shown in chat — includes feedback overrides (not helpful → disappointed). */
export function resolveOnyxMessageEmotion(
  emotion: OnyxEmotion | undefined,
  feedback?: 'helpful' | 'not_helpful' | null
): OnyxEmotion {
  if (feedback === 'not_helpful') return 'DISAPPOINTED';
  if (feedback === 'helpful') return 'HAPPY';
  return emotion ?? 'NEUTRAL';
}

export const ONYX_POKE_LINES = [
  "Hey! Don't poke me.",
  "Don't poke me.",
  'Hands off.',
  'Stop poking.',
  'I felt that. Not cool.',
  "I'm working here — don't poke.",
  'Easy. No poking.',
  'Do not poke me.',
] as const;

/** Phase 2 after poke — softens to “ok” face / playful recovery. */
export const ONYX_POKE_OK_LINES = [
  'Ok ok… I’m fine. How can I help?',
  'Alright, truce. What do you need?',
  'We’re good. Ask me anything.',
  'Deep breath. Ready when you are.',
  'Forgiven. What’s up?',
  'Fine — you win. How can I help you?',
] as const;

/** Timed poke theatre: sulky (hurt) → straighten with thumbs up (proud). FAB bubble only — never chat. */
export const ONYX_POKE_SEQUENCE = {
  cooldownMs: 3800,
  hurtFaceMs: 0,
  hurtLineMs: 200,
  thumbsMs: 2000,
  okLineMs: 2000,
  settleMs: 5800,
} as const;

/**
 * Idle → bed: after silence in open chat, Onyx says a line then returns to sleep.
 * EN defaults; UI uses support:helpChat.onyxBedtime1..3 (i18n).
 */
export const ONYX_BEDTIME_LINES = [
  "Alright — if you don't want to talk, I'm heading back to bed.",
  "I'll catch some Z's. Wake me up when you need me.",
  "Going back to sleep. Ping me if you need anything.",
] as const;

/** Silence in open chat before bedtime line + sleep (ms). */
export const ONYX_BEDTIME_IDLE_MS = 60_000;

/**
 * Wake FAB dialogue (bubble ONLY — never mirrored into chat).
 * Grammar: “You woke me up, dude.” then language ask.
 */
export const ONYX_WAKE_BUBBLE = {
  /** Phase 1 — sulky (finish speaking + writing before phase 2) */
  wokeUp: 'You woke me up, dude.',
  /** Phase 2 — thumbs + flags · invite chat (i18n: support:helpChat.onyxWakeAsk) */
  askNeed: 'What can I do for you? Type it in the chat.',
  /** Pause after phrase 1 ends, before phrase 2 */
  gapAfterPhrase1Ms: 450,
  /** Hold phrase 2 on screen after speech ends */
  holdPhrase2Ms: 900,
} as const;

/** @deprecated Prefer ONYX_WAKE_BUBBLE for FAB wake. Kept for rare fallbacks. */
export const ONYX_WAKE_LINES = [
  ONYX_WAKE_BUBBLE.wokeUp,
  ONYX_WAKE_BUBBLE.askNeed,
] as const;

export function pickRandomLine(lines: readonly string[]): string {
  return lines[Math.floor(Math.random() * lines.length)] || lines[0];
}

/** All emotion image URLs — useful for preloading. */
export function onyxAllImageUrls(): string[] {
  return ONYX_EMOTIONS.map((e) => onyxImageUrl(e));
}
